'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from 'react';
import { useConnection, usePublicClient, useWalletClient } from 'wagmi';
import { parseUnits } from 'viem';
import { writeContract, readContract, waitForTransactionReceipt } from '@wagmi/core';
import { config } from '@/config/index';
import { useRouterFunctions } from './useRouterFunctions';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import toast from '@/util/toast';
import { useTokenApproval } from './useTokenApproval';
import { Token, CurrencyAmount, Percent } from '@chewy/swap-sdk-core';
import { ChainType, chainSettings } from '@/constants/chain-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import type { LiquidityPosition } from './useLiquidityPositions';
export interface AddLiquidityParams {
  token0: TokenConfigType;
  token1: TokenConfigType;
  amount0: string;
  amount1: string;
  dexSlug: string;
  version: 'v2' | 'v3';
  tickLower?: number; // V3 only
  tickUpper?: number; // V3 only
  slippage: number; // Percentage (0.5 = 0.5%)
  deadline?: number; // Unix timestamp (optional, defaults to 20 minutes)
  priceRange?: {
    lower: number;
    upper: number;
    current: number;
  };
}
export interface RemoveLiquidityParams {
  position: LiquidityPosition;
  percentage: number; // 0-100
  slippageTolerance: number;
  deadline: number;
  isTaxToken?: boolean; // For tokens with transfer fees
  receiveNative?: boolean; // If true, unwrap wrapped native to native (e.g., WBONE -> BONE)
}
export interface LiquidityManagementState {
  isLoading: boolean;
  error: string | null;
  txHash: string | null;
  step: 'idle' | 'approval' | 'transaction' | 'confirmed';
}

/**
 * Hook for managing liquidity operations (add/remove)
 */
export function useLiquidityManagement(chainId?: ChainType) {
  const {
    address
  } = useConnection();
  const publicClient = usePublicClient();
  const {
    data: walletClient
  } = useWalletClient();
  const {
    ensureApprovals,
    state: approvalState
  } = useTokenApproval();
  const {
    getAddLiquidityCall,
    getRemoveLiquidityCall,
    getRouterConfig
  } = useRouterFunctions(chainId || 109);
  const [state, setState] = useState<LiquidityManagementState>({
    isLoading: false,
    error: null,
    txHash: null,
    step: 'idle'
  });
  const resetState = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      txHash: null,
      step: 'idle'
    });
  }, []);
  const setError = useCallback((error: string) => {
    setState(prev => ({
      ...prev,
      error,
      isLoading: false,
      step: 'idle'
    }));
    toast.error(error);
  }, []);

  /**
   * Add liquidity to a pool
   */
  const addLiquidity = useCallback(async (params: AddLiquidityParams) => {
    if (!address || !walletClient || !publicClient) {
      setError('Wallet not connected');
      return;
    }
    try {
      setState(prev_0 => ({
        ...prev_0,
        isLoading: true,
        error: null,
        step: 'approval'
      }));
      const routerConfig = getRouterConfig(params.dexSlug);
      console.log('🔄 Starting add liquidity transaction...');
      console.log('Parameters:', params);

      // Get the appropriate router call data
      const routerCall = getAddLiquidityCall({
        token0: params.token0,
        token1: params.token1,
        amount0: params.amount0,
        amount1: params.amount1,
        slippage: params.slippage,
        deadline: params.deadline || Math.floor(Date.now() / 1000) + 1200,
        to: address,
        dexSlug: params.dexSlug,
        // Pass dexSlug for velodrome-type DEX detection
        stable: false // Default to volatile pairs (can be made configurable later)
      });
      console.log('🎯 Router call:', routerCall);

      // Handle token approvals (only for ERC20 tokens, native tokens don't need approval)
      const approvalsNeeded = [];
      if (routerCall.functionName.includes('ETH')) {
        // For ETH functions, only approve the ERC20 token
        const nonNativeToken = routerCall.requiresNativeETH ? params.token0.address === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' ? params.token1 : params.token0 : params.token0;
        const nonNativeAmount = routerCall.requiresNativeETH ? params.token0.address === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' ? params.amount1 : params.amount0 : params.amount0;
        approvalsNeeded.push({
          token: nonNativeToken,
          amount: nonNativeAmount,
          spender: routerConfig.address
        });
      } else {
        // For regular addLiquidity, approve both tokens
        approvalsNeeded.push({
          token: params.token0,
          amount: params.amount0,
          spender: routerConfig.address
        }, {
          token: params.token1,
          amount: params.amount1,
          spender: routerConfig.address
        });
      }
      if (approvalsNeeded.length > 0) {
        console.log('🔄 Ensuring token approvals...');
        // Note: This uses the old approval interface, may need adjustment
        await ensureApprovals(params.token0, params.token1, params.amount0, params.amount1, params.dexSlug, chainId || 109);
      }
      setState(prev_1 => ({
        ...prev_1,
        step: 'transaction'
      }));

      // Execute the transaction
      const txHash = await writeContract(config, {
        address: routerConfig.address,
        abi: routerConfig.abi,
        functionName: routerCall.functionName,
        args: routerCall.args,
        value: routerCall.value || 0n,
        chainId: Number(chainId || 109)
      });
      console.log('✅ Transaction sent:', txHash);
      setState(prev_2 => ({
        ...prev_2,
        txHash,
        step: 'confirmed',
        isLoading: false
      }));
      toast.success(`Liquidity added successfully! ${routerCall.description}`);
      return txHash;
    } catch (error_0) {
      console.error('Add liquidity error:', error_0);
      setError(error_0 instanceof Error ? error_0.message : 'Failed to add liquidity');
    }
  }, [address, walletClient, publicClient, setError, getAddLiquidityCall, getRouterConfig, ensureApprovals, chainId]);

  /**
   * Remove liquidity from a position
   */
  const removeLiquidity = useCallback(async (params_0: RemoveLiquidityParams) => {
    if (!address || !walletClient || !publicClient) {
      setError('Wallet not connected');
      return;
    }
    try {
      setState(prev_3 => ({
        ...prev_3,
        isLoading: true,
        error: null,
        step: 'approval'
      }));
      if (params_0.position.version === 'v3') {
        await removeV3Liquidity(params_0);
      } else {
        await removeV2Liquidity(params_0);
      }
      setState(prev_4 => ({
        ...prev_4,
        step: 'confirmed',
        isLoading: false
      }));
      toast.success('Liquidity removed successfully!');
    } catch (error_1) {
      console.error('Remove liquidity error:', error_1);
      setError(error_1 instanceof Error ? error_1.message : 'Failed to remove liquidity');
    }
  }, [address, walletClient, publicClient, setError]);

  /**
   * Add V3 liquidity using Uniswap V3 SDK
   */
  const addV3Liquidity = async (params_1: AddLiquidityParams) => {
    if (!chainId) throw new Error('Chain ID not available');
    const {
      token0,
      token1,
      amount0,
      amount1,
      tickLower = -887220,
      tickUpper = 887220,
      slippage,
      deadline,
      dexSlug
    } = params_1;
    console.log('🔄 Adding V3 liquidity:', {
      token0: token0.symbol,
      token1: token1.symbol,
      amount0,
      amount1,
      tickLower,
      tickUpper,
      slippage,
      deadline
    });
    try {
      // Create Token instances
      const tokenA = new Token(Number(chainId), token0.address as `0x${string}`, token0.decimals, token0.symbol, token0.name);
      const tokenB = new Token(Number(chainId), token1.address as `0x${string}`, token1.decimals, token1.symbol, token1.name);

      // Parse amounts
      const amount0Parsed = CurrencyAmount.fromRawAmount(tokenA, parseUnits(amount0, token0.decimals).toString());
      const amount1Parsed = CurrencyAmount.fromRawAmount(tokenB, parseUnits(amount1, token1.decimals).toString());

      // Get DEX configuration
      const dex = DEX_LIST[dexSlug];
      if (!dex || dex.type !== 'v3') throw new Error(`V3 DEX ${dexSlug} not found`);

      // Calculate minimum amounts with slippage
      const slippagePercent = new Percent(Math.floor(slippage * 100), 10000);
      const amount0Min = amount0Parsed.multiply(new Percent(10000 - Math.floor(slippage * 100), 10000));
      const amount1Min = amount1Parsed.multiply(new Percent(10000 - Math.floor(slippage * 100), 10000));

      // Prepare transaction
      const deadlineTimestamp = Math.floor(Date.now() / 1000) + (deadline || 1200);
      const fee = 3000; // 0.3% fee tier (most common)

      setState(prev_5 => ({
        ...prev_5,
        step: 'transaction'
      }));
      const txHash_0 = await writeContract(config, {
        address: dex.router.address as `0x${string}`,
        abi: dex.router.abi,
        functionName: 'mint',
        args: [{
          token0: tokenA.address,
          token1: tokenB.address,
          fee,
          tickLower,
          tickUpper,
          amount0Desired: amount0Parsed.quotient.toString(),
          amount1Desired: amount1Parsed.quotient.toString(),
          amount0Min: amount0Min.quotient.toString(),
          amount1Min: amount1Min.quotient.toString(),
          recipient: address,
          deadline: deadlineTimestamp
        }],
        chainId
      });
      setState(prev_6 => ({
        ...prev_6,
        txHash: txHash_0
      }));
      console.log('✅ V3 liquidity added successfully:', txHash_0);
    } catch (error_2) {
      console.error('❌ V3 liquidity addition failed:', error_2);
      throw error_2;
    }
  };

  /**
   * Add V2 liquidity
   */
  const addV2Liquidity = async (params_2: AddLiquidityParams) => {
    if (!chainId) throw new Error('Chain ID not available');
    const {
      token0: token0_0,
      token1: token1_0,
      amount0: amount0_0,
      amount1: amount1_0,
      slippage: slippage_0,
      deadline: deadline_0,
      dexSlug: dexSlug_0
    } = params_2;
    console.log('🔄 Adding V2 liquidity:', {
      token0: token0_0.symbol,
      token1: token1_0.symbol,
      amount0: amount0_0,
      amount1: amount1_0,
      slippage: slippage_0,
      deadline: deadline_0
    });
    try {
      // Create Token instances
      const tokenA_0 = new Token(Number(chainId), token0_0.address as `0x${string}`, token0_0.decimals, token0_0.symbol, token0_0.name);
      const tokenB_0 = new Token(Number(chainId), token1_0.address as `0x${string}`, token1_0.decimals, token1_0.symbol, token1_0.name);

      // Parse amounts
      const amount0Parsed_0 = CurrencyAmount.fromRawAmount(tokenA_0, parseUnits(amount0_0, token0_0.decimals).toString());
      const amount1Parsed_0 = CurrencyAmount.fromRawAmount(tokenB_0, parseUnits(amount1_0, token1_0.decimals).toString());

      // Get DEX router contract
      const dex_0 = DEX_LIST[dexSlug_0];
      if (!dex_0) throw new Error(`DEX ${dexSlug_0} not found`);

      // Check if this is a velodrome-type DEX (requires stable parameter)
      const isVelodromeType = dex_0.type === 'velodrome';

      // Calculate minimum amounts with slippage
      const slippagePercent_0 = new Percent(Math.floor(slippage_0 * 100), 10000);
      const amount0Min_0 = amount0Parsed_0.multiply(new Percent(10000 - Math.floor(slippage_0 * 100), 10000));
      const amount1Min_0 = amount1Parsed_0.multiply(new Percent(10000 - Math.floor(slippage_0 * 100), 10000));

      // Prepare transaction data
      const deadlineTimestamp_0 = Math.floor(Date.now() / 1000) + (deadline_0 || 1200); // 20 minutes default

      setState(prev_7 => ({
        ...prev_7,
        step: 'transaction'
      }));

      // Build args based on DEX type
      // Velodrome addLiquidity: (tokenA, tokenB, stable, amountADesired, amountBDesired, amountAMin, amountBMin, to, deadline)
      // Standard V2 addLiquidity: (tokenA, tokenB, amountADesired, amountBDesired, amountAMin, amountBMin, to, deadline)
      const args = isVelodromeType ? [tokenA_0.address, tokenB_0.address, false,
      // stable = false for volatile pairs (can be made configurable)
      amount0Parsed_0.quotient.toString(), amount1Parsed_0.quotient.toString(), amount0Min_0.quotient.toString(), amount1Min_0.quotient.toString(), address, deadlineTimestamp_0] : [tokenA_0.address, tokenB_0.address, amount0Parsed_0.quotient.toString(), amount1Parsed_0.quotient.toString(), amount0Min_0.quotient.toString(), amount1Min_0.quotient.toString(), address, deadlineTimestamp_0];

      // Use writeContract to add liquidity
      const txHash_1 = await writeContract(config, {
        address: dex_0.router.address as `0x${string}`,
        abi: dex_0.router.abi,
        functionName: 'addLiquidity',
        args,
        chainId
      });
      setState(prev_8 => ({
        ...prev_8,
        txHash: txHash_1
      }));
      console.log('✅ V2 liquidity added successfully:', txHash_1);
    } catch (error_3) {
      console.error('❌ V2 liquidity addition failed:', error_3);
      throw error_3;
    }
  };

  /**
   * Remove V3 liquidity
   */
  const removeV3Liquidity = async (params_3: RemoveLiquidityParams) => {
    if (!chainId) throw new Error('Chain ID not available');
    const {
      position,
      percentage,
      slippageTolerance,
      deadline: deadline_1
    } = params_3;
    console.log('🔄 Removing V3 liquidity:', {
      positionId: position.id,
      percentage,
      slippageTolerance
    });
    try {
      // Get DEX configuration
      const dex_1 = DEX_LIST[position.dex];
      if (!dex_1 || dex_1.type !== 'v3') throw new Error(`V3 DEX ${position.dex} not found`);
      const nftManagerAddress = dex_1.router.address as `0x${string}`;
      const tokenId = position.tokenId || position.id;

      // CRITICAL: Query actual position liquidity from NonfungiblePositionManager
      // This ensures we use the exact on-chain liquidity value
      const positionInfo = (await readContract(config, {
        address: nftManagerAddress,
        abi: [{
          inputs: [{
            name: 'tokenId',
            type: 'uint256'
          }],
          name: 'positions',
          outputs: [{
            name: 'nonce',
            type: 'uint96'
          }, {
            name: 'operator',
            type: 'address'
          }, {
            name: 'token0',
            type: 'address'
          }, {
            name: 'token1',
            type: 'address'
          }, {
            name: 'fee',
            type: 'uint24'
          }, {
            name: 'tickLower',
            type: 'int24'
          }, {
            name: 'tickUpper',
            type: 'int24'
          }, {
            name: 'liquidity',
            type: 'uint128'
          }, {
            name: 'feeGrowthInside0LastX128',
            type: 'uint256'
          }, {
            name: 'feeGrowthInside1LastX128',
            type: 'uint256'
          }, {
            name: 'tokensOwed0',
            type: 'uint128'
          }, {
            name: 'tokensOwed1',
            type: 'uint128'
          }],
          stateMutability: 'view',
          type: 'function'
        }],
        functionName: 'positions',
        args: [BigInt(tokenId)],
        chainId: Number(chainId)
      })) as readonly [bigint, string, string, string, number, number, number, bigint, bigint, bigint, bigint, bigint];
      const onChainLiquidity = positionInfo[7]; // liquidity is at index 7
      console.log('📊 On-chain V3 liquidity:', onChainLiquidity.toString());
      if (onChainLiquidity === 0n) {
        throw new Error('No liquidity found in position');
      }

      // For 100% removal, use exact on-chain liquidity
      // For partial removal, calculate percentage from on-chain liquidity
      const liquidityToRemove = percentage === 100 ? onChainLiquidity : onChainLiquidity * BigInt(percentage) / BigInt(100);
      console.log('💧 Liquidity to remove:', liquidityToRemove.toString());

      // For V3, set minimum amounts to 0 - the contract calculates actual amounts
      // based on current price. Slippage protection is handled by the pool itself.
      const amount0Min_1 = 0n;
      const amount1Min_1 = 0n;
      const deadlineTimestamp_1 = Math.floor(Date.now() / 1000) + (deadline_1 || 1200);
      setState(prev_9 => ({
        ...prev_9,
        step: 'transaction'
      }));

      // For V3, we need to use decreaseLiquidity and then collect
      const txHash_2 = await writeContract(config, {
        address: nftManagerAddress,
        abi: dex_1.router.abi,
        functionName: 'decreaseLiquidity',
        args: [{
          tokenId: BigInt(tokenId),
          liquidity: liquidityToRemove,
          amount0Min: amount0Min_1,
          amount1Min: amount1Min_1,
          deadline: deadlineTimestamp_1
        }],
        chainId
      });
      setState(prev_10 => ({
        ...prev_10,
        txHash: txHash_2
      }));
      console.log('✅ V3 liquidity removed successfully:', txHash_2);
    } catch (error_4) {
      console.error('❌ V3 liquidity removal failed:', error_4);
      throw error_4;
    }
  };

  /**
   * Remove V2 liquidity
   */
  const removeV2Liquidity = async (params_4: RemoveLiquidityParams) => {
    if (!chainId) throw new Error('Chain ID not available');
    const {
      position: position_0,
      percentage: percentage_0,
      slippageTolerance: slippageTolerance_0,
      deadline: deadline_2,
      isTaxToken,
      receiveNative = true
    } = params_4;
    console.log('🔄 Removing V2 liquidity:', {
      positionId: position_0.id,
      percentage: percentage_0,
      slippageTolerance: slippageTolerance_0,
      isTaxToken,
      receiveNative
    });
    try {
      // Get DEX configuration first (needed for router address)
      const dex_2 = DEX_LIST[position_0.dex];
      if (!dex_2) throw new Error(`DEX ${position_0.dex} not found`);

      // LP token address - use pair address or position id
      const lpTokenAddress = (position_0.pair || position_0.pairAddress || position_0.contractAddress || position_0.id) as `0x${string}`;
      console.log('🔍 LP token address:', lpTokenAddress);

      // CRITICAL: Query actual LP token balance from chain for accurate removal
      // This ensures we use the exact on-chain balance, avoiding precision issues
      const onChainBalance = (await readContract(config, {
        address: lpTokenAddress,
        abi: [{
          constant: true,
          inputs: [{
            name: 'account',
            type: 'address'
          }],
          name: 'balanceOf',
          outputs: [{
            name: '',
            type: 'uint256'
          }],
          type: 'function'
        }],
        functionName: 'balanceOf',
        args: [address],
        chainId: Number(chainId)
      })) as bigint;
      console.log('📊 On-chain LP balance:', onChainBalance.toString());
      if (onChainBalance === 0n) {
        throw new Error('No LP tokens found in wallet');
      }

      // For 100% removal, use exact on-chain balance
      // For partial removal, calculate percentage from on-chain balance
      const liquidityToRemove_0 = percentage_0 === 100 ? onChainBalance : onChainBalance * BigInt(percentage_0) / BigInt(100);
      console.log('💧 Liquidity to remove:', liquidityToRemove_0.toString());

      // Calculate expected token amounts from reserves and user's share
      // Query pool reserves to calculate expected output
      const [reserve0, reserve1, totalSupply] = await Promise.all([readContract(config, {
        address: lpTokenAddress,
        abi: [{
          constant: true,
          inputs: [],
          name: 'getReserves',
          outputs: [{
            name: '',
            type: 'uint112'
          }, {
            name: '',
            type: 'uint112'
          }, {
            name: '',
            type: 'uint32'
          }],
          type: 'function'
        }],
        functionName: 'getReserves',
        chainId: Number(chainId)
      }).then((res: any) => BigInt(res[0])), readContract(config, {
        address: lpTokenAddress,
        abi: [{
          constant: true,
          inputs: [],
          name: 'getReserves',
          outputs: [{
            name: '',
            type: 'uint112'
          }, {
            name: '',
            type: 'uint112'
          }, {
            name: '',
            type: 'uint32'
          }],
          type: 'function'
        }],
        functionName: 'getReserves',
        chainId: Number(chainId)
      }).then((res_0: any) => BigInt(res_0[1])), readContract(config, {
        address: lpTokenAddress,
        abi: [{
          constant: true,
          inputs: [],
          name: 'totalSupply',
          outputs: [{
            name: '',
            type: 'uint256'
          }],
          type: 'function'
        }],
        functionName: 'totalSupply',
        chainId: Number(chainId)
      }) as Promise<bigint>]);

      // Calculate expected token amounts based on liquidity being removed
      const amount0ToRemove = totalSupply > 0n ? reserve0 * liquidityToRemove_0 / totalSupply : 0n;
      const amount1ToRemove = totalSupply > 0n ? reserve1 * liquidityToRemove_0 / totalSupply : 0n;

      // Calculate minimum amounts with slippage (using basis points: 10000 = 100%)
      // slippageTolerance is in percentage (e.g., 1 = 1%), so convert to basis points
      const slippageBps = Math.floor(slippageTolerance_0 * 100); // 1% = 100 bps
      const amount0Min_2 = amount0ToRemove * BigInt(10000 - slippageBps) / BigInt(10000);
      const amount1Min_2 = amount1ToRemove * BigInt(10000 - slippageBps) / BigInt(10000);
      console.log('📊 Expected amounts:', {
        amount0ToRemove: amount0ToRemove.toString(),
        amount1ToRemove: amount1ToRemove.toString()
      });
      console.log('📊 Min amounts (with slippage):', {
        amount0Min: amount0Min_2.toString(),
        amount1Min: amount1Min_2.toString()
      });
      const deadlineTimestamp_2 = Math.floor(Date.now() / 1000) + (deadline_2 || 1200);

      // Check if this is a velodrome-type DEX (requires stable parameter)
      const isVelodromeType_0 = dex_2.type === 'velodrome';

      // Determine wrapped native address for this DEX
      // Use DEX-specific weth if available, otherwise chain's default wNative
      const chainConfig = chainSettings[chainId];
      const wrappedNativeAddress = (dex_2.weth?.address || chainConfig?.wNative?.address)?.toLowerCase();

      // Check if either token is the wrapped native (for removeLiquidityETH)
      const token0IsWrappedNative = wrappedNativeAddress && position_0.token0.address.toLowerCase() === wrappedNativeAddress;
      const token1IsWrappedNative = wrappedNativeAddress && position_0.token1.address.toLowerCase() === wrappedNativeAddress;
      const hasWrappedNative = token0IsWrappedNative || token1IsWrappedNative;
      console.log('🔍 Checking LP token approval for:', lpTokenAddress);

      // Read current allowance
      const allowance = await readContract(config, {
        address: lpTokenAddress as `0x${string}`,
        abi: [{
          constant: true,
          inputs: [{
            name: 'owner',
            type: 'address'
          }, {
            name: 'spender',
            type: 'address'
          }],
          name: 'allowance',
          outputs: [{
            name: '',
            type: 'uint256'
          }],
          type: 'function'
        }],
        functionName: 'allowance',
        args: [address, dex_2.router.address],
        chainId: Number(chainId)
      });

      // If allowance is insufficient, request approval
      if (BigInt(allowance as bigint) < liquidityToRemove_0) {
        console.log('🔐 LP token approval needed');
        setState(prev_11 => ({
          ...prev_11,
          step: 'approval'
        }));
        const approveTxHash = await writeContract(config, {
          address: lpTokenAddress as `0x${string}`,
          abi: [{
            constant: false,
            inputs: [{
              name: 'spender',
              type: 'address'
            }, {
              name: 'amount',
              type: 'uint256'
            }],
            name: 'approve',
            outputs: [{
              name: '',
              type: 'bool'
            }],
            type: 'function'
          }],
          functionName: 'approve',
          args: [dex_2.router.address, liquidityToRemove_0],
          chainId: Number(chainId)
        });
        console.log('✅ LP token approved:', approveTxHash);

        // Wait for approval confirmation
        await waitForTransactionReceipt(config, {
          hash: approveTxHash,
          chainId: Number(chainId)
        });
      }
      setState(prev_12 => ({
        ...prev_12,
        step: 'transaction'
      }));
      let txHash_3: `0x${string}`;

      // Use removeLiquidityETH if pair contains wrapped native AND user wants to receive native
      if (hasWrappedNative && receiveNative) {
        // Determine which token is the non-wrapped token
        const token = token0IsWrappedNative ? position_0.token1 : position_0.token0;
        const amountTokenMin = token0IsWrappedNative ? amount1Min_2 : amount0Min_2;
        const amountETHMin = token0IsWrappedNative ? amount0Min_2 : amount1Min_2;
        if (isTaxToken) {
          // Use removeLiquidityETHSupportingFeeOnTransferTokens for tax tokens
          // Velodrome: (token, stable, liquidity, amountTokenMin, amountETHMin, to, deadline)
          // Standard V2: (token, liquidity, amountTokenMin, amountETHMin, to, deadline)
          console.log('🔥 Using removeLiquidityETHSupportingFeeOnTransferTokens (wrapped native -> native)');
          const args_0 = isVelodromeType_0 ? [token.address, false,
          // stable = false for volatile pairs
          liquidityToRemove_0, amountTokenMin, amountETHMin, address, deadlineTimestamp_2] : [token.address, liquidityToRemove_0, amountTokenMin, amountETHMin, address, deadlineTimestamp_2];
          txHash_3 = await writeContract(config, {
            address: dex_2.router.address as `0x${string}`,
            abi: dex_2.router.abi,
            functionName: 'removeLiquidityETHSupportingFeeOnTransferTokens',
            args: args_0,
            chainId: Number(chainId)
          });
        } else {
          // Use removeLiquidityETH for regular tokens
          // Velodrome: (token, stable, liquidity, amountTokenMin, amountETHMin, to, deadline)
          // Standard V2: (token, liquidity, amountTokenMin, amountETHMin, to, deadline)
          console.log('🔥 Using removeLiquidityETH (wrapped native -> native)');
          const args_1 = isVelodromeType_0 ? [token.address, false,
          // stable = false for volatile pairs
          liquidityToRemove_0, amountTokenMin, amountETHMin, address, deadlineTimestamp_2] : [token.address, liquidityToRemove_0, amountTokenMin, amountETHMin, address, deadlineTimestamp_2];
          txHash_3 = await writeContract(config, {
            address: dex_2.router.address as `0x${string}`,
            abi: dex_2.router.abi,
            functionName: 'removeLiquidityETH',
            args: args_1,
            chainId: Number(chainId)
          });
        }
      } else if (isTaxToken) {
        // Both tokens are ERC20 (or user wants wrapped native) AND one has transfer fee
        // Use removeLiquiditySupportingFeeOnTransferTokens
        // Note: This function doesn't exist in standard Uniswap V2, but some forks have it
        // Velodrome: (tokenA, tokenB, stable, liquidity, amountAMin, amountBMin, to, deadline)
        // Standard V2: (tokenA, tokenB, liquidity, amountAMin, amountBMin, to, deadline)
        console.log('🔥 Using removeLiquidity with fee-on-transfer support (ERC20 tokens with tax)');
        const args_2 = isVelodromeType_0 ? [position_0.token0.address, position_0.token1.address, false,
        // stable = false for volatile pairs
        liquidityToRemove_0, amount0Min_2, amount1Min_2, address, deadlineTimestamp_2] : [position_0.token0.address, position_0.token1.address, liquidityToRemove_0, amount0Min_2, amount1Min_2, address, deadlineTimestamp_2];

        // Try removeLiquiditySupportingFeeOnTransferTokens first, fall back to removeLiquidity
        try {
          txHash_3 = await writeContract(config, {
            address: dex_2.router.address as `0x${string}`,
            abi: dex_2.router.abi,
            functionName: 'removeLiquiditySupportingFeeOnTransferTokens',
            args: args_2,
            chainId: Number(chainId)
          });
        } catch (feeOnTransferError) {
          // If the router doesn't support this function, fall back to standard removeLiquidity
          console.log('⚠️ removeLiquiditySupportingFeeOnTransferTokens not available, using removeLiquidity');
          txHash_3 = await writeContract(config, {
            address: dex_2.router.address as `0x${string}`,
            abi: dex_2.router.abi,
            functionName: 'removeLiquidity',
            args: args_2,
            chainId: Number(chainId)
          });
        }
      } else {
        // Both tokens are ERC20 or user wants to receive wrapped native (no tax token)
        // Velodrome: (tokenA, tokenB, stable, liquidity, amountAMin, amountBMin, to, deadline)
        // Standard V2: (tokenA, tokenB, liquidity, amountAMin, amountBMin, to, deadline)
        console.log('🔥 Using removeLiquidity (receiving ERC20 tokens)');
        const args_3 = isVelodromeType_0 ? [position_0.token0.address, position_0.token1.address, false,
        // stable = false for volatile pairs
        liquidityToRemove_0, amount0Min_2, amount1Min_2, address, deadlineTimestamp_2] : [position_0.token0.address, position_0.token1.address, liquidityToRemove_0, amount0Min_2, amount1Min_2, address, deadlineTimestamp_2];
        txHash_3 = await writeContract(config, {
          address: dex_2.router.address as `0x${string}`,
          abi: dex_2.router.abi,
          functionName: 'removeLiquidity',
          args: args_3,
          chainId: Number(chainId)
        });
      }
      setState(prev_13 => ({
        ...prev_13,
        txHash: txHash_3
      }));
      console.log('✅ V2 liquidity removed successfully:', txHash_3);
    } catch (error_5) {
      console.error('❌ V2 liquidity removal failed:', error_5);
      throw error_5;
    }
  };
  return {
    addLiquidity,
    removeLiquidity,
    state,
    resetState
  };
}

/**
 * Hook for price calculations and estimates
 */
export function useLiquidityCalculations() {
  const $ = _c(1);
  const calculateV3Position = _temp;
  const calculateV2Position = _temp2;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      calculateV3Position,
      calculateV2Position
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(token0_0, token1_0, amount0_0, amount1_0, reserves0, reserves1) {
  return {
    token0Amount: amount0_0,
    token1Amount: amount1_0,
    lpTokens: "0",
    shareOfPool: 0
  };
}
function _temp(token0, token1, amount0, amount1, tickLower, tickUpper, currentTick) {
  return {
    token0Amount: amount0,
    token1Amount: amount1,
    liquidityAmount: "0",
    priceRange: {
      lower: "0",
      upper: "0",
      current: "0"
    },
    inRange: currentTick >= tickLower && currentTick <= tickUpper
  };
}